import { Bell, BellOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { usePushNotifications } from '@/hooks/usePushNotifications';

interface PushNotificationToggleProps {
  variant?: 'button' | 'switch';
  showLabel?: boolean;
}

export function PushNotificationToggle({ 
  variant = 'switch',
  showLabel = true 
}: PushNotificationToggleProps) {
  const { 
    isSupported, 
    isSubscribed, 
    isLoading, 
    permission,
    toggleSubscription 
  } = usePushNotifications();

  if (!isSupported) {
    return (
      <div className="text-sm text-muted-foreground">
        Push notifications are not supported in this browser.
      </div>
    );
  }

  if (variant === 'button') {
    return (
      <Button
        variant={isSubscribed ? "outline" : "default"}
        size="sm"
        onClick={toggleSubscription}
        disabled={isLoading}
        className="gap-2"
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : isSubscribed ? (
          <BellOff className="h-4 w-4" />
        ) : (
          <Bell className="h-4 w-4" />
        )}
        {isSubscribed ? 'Disable Notifications' : 'Enable Notifications'}
      </Button>
    );
  }

  return (
    <div className="flex items-center justify-between space-x-4">
      <div className="space-y-0.5">
        {showLabel && (
          <Label htmlFor="push-notifications" className="text-base">
            Push Notifications
          </Label>
        )}
        <p className="text-sm text-muted-foreground">
          {isSubscribed 
            ? 'You will receive browser notifications for important updates.'
            : permission === 'denied'
            ? 'Notifications are blocked. Please enable them in your browser settings.'
            : 'Enable to receive real-time browser notifications.'}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        <Switch
          id="push-notifications"
          checked={isSubscribed}
          onCheckedChange={toggleSubscription}
          disabled={isLoading || permission === 'denied'}
        />
      </div>
    </div>
  );
}
